import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { EntryPointElement } from '@databox/ember-integrations/lib/components/Setup';

import type { ReactIntegrations, Service } from 'webapp-types';
import type { TrackingEvent } from 'webapp/enums/tracking';
import type { UserSettingsKey } from 'webapp/enums/user-settings';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

// Props handed to a feature's "coming soon" React entrypoint. The webapp owns the waitlist state
// (read/write of the user setting) and passes it down; the entrypoint only renders and calls back.
export interface ComingSoonEntryPointProps {
	isDarkThemeActive: boolean;
	isOnWaitingList: boolean;
	addToWaitingList: () => void;
}

// A feature-specific entrypoint from `@databox/ember-integrations` (a React FC is assignable to this).
export type ComingSoonEntryPoint = (props: ComingSoonEntryPointProps) => EntryPointElement;

interface CommonComingSoonPageSignature {
	Element: HTMLDivElement;
	Args: {
		settingKey: UserSettingsKey;
		eventName: TrackingEvent;
		entryPoint: ComingSoonEntryPoint;
	};
}

export default class CommonComingSoonPageComponent extends Component<CommonComingSoonPageSignature> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;

	get isOnWaitingList(): boolean {
		return !!this.userSettingsService.userSettings[this.args.settingKey];
	}

	@action
	setupView(node: HTMLElement) {
		mountReactRoot(this, node, () => this.renderComingSoon());
	}

	@action
	teardownView() {
		unmountReactRoot(this);
	}

	@action
	addToWaitingList() {
		if (this.isOnWaitingList) {
			return;
		}

		void this.userSettingsService.save(this.args.settingKey, true);
		this.analyticsService.event(this.args.eventName);

		// Re-render the React tree so the updated `isOnWaitingList` flows into the mounted entrypoint.
		this.renderComingSoon();
	}

	private renderComingSoon() {
		if (!this.reactElement) {
			return;
		}

		const props: ComingSoonEntryPointProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			isOnWaitingList: this.isOnWaitingList,
			addToWaitingList: this.addToWaitingList,
		};

		this.reactService.render(
			this.reactElement,
			this.args.entryPoint(props),
			getShadowDomMode(this.accountService.email),
		);
	}

	<template>
		<div {{didInsert this.setupView}} {{willDestroy this.teardownView}} ...attributes />
	</template>
}
